/* eslint-disable react/no-children-prop */
import { memo, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import PassengersCounterDisplay from "app/pages/.shared/PassengersCounterDisplay";
import { FormattedDate, FormattedMessage, FormattedPlural } from "react-intl";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import TravelDatesContainer from "app/pages/.shared/TravelDates/TravelDatesContainer";
import TransferIcon from "app/pages/.shared/IconNegoTransfer";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import Collapse from "rc-collapse";
import MyFlightDetail from "app/pages/Booking/MyFlightDetail";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import AppGlobalsContext from "app/AppGlobalsContext";
import OccupanciesDisplayLabel from "app/pages/.shared/OccupanciesDisplayLabel/OccupanciesDisplayLabel";
import NegociatedItem from "app/pages/FicheProduit/NegociatedList/NegociatedItem";
import "./HolidaySummary.scss";
import {
	NEGOCIATED_TYPES,
	OFFER_TYPES,
	PRICE_CHANGE_STATUS,
	PRODUCT_EXPERIENCES,
	QUOTATION_CODE_STATUS,
} from "app/constants";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import Timer from "app/pages/.shared/Timer";
import Badge from "app/pages/.shared/Badge";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import classNames from "classnames";
import IconFlight from "app/pages/.shared/IconFlight";
import isEmpty from "lodash/isEmpty";
import IconClockNotification from "app/pages/.shared/static/icons/IconClockNotification";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useFsFlag } from "@flagship.io/react-sdk";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import ProductSummaryContainer from "app/pages/.shared/ProductSummary/ProductSummaryContainer";
import { parseDepartureCityLabel } from "app/utils/utils";
import IconCalendar from "app/pages/.shared/IconCalendar";
import { addDays } from "date-fns";
import AccommodationSummaryList from "app/pages/HotelOnly/components/AccommodationSummaryList/AccommodationSummaryList";

const IMG_SIZES_DESKTOP = {
	xlarge: { height: 201, width: 348 },
	large: { height: 201, width: 348 },
};

const IMG_SIZES_MOBILE = {
	medium: { width: 800 },
	small: { width: 800 },
};

const NEGOS_TO_EXCLUDE = [
	NEGOCIATED_TYPES.SPECIAL_OFFER,
	NEGOCIATED_TYPES.ACCOMMODATION,
	NEGOCIATED_TYPES.BOARD,
	NEGOCIATED_TYPES.CAR_RENTAL,
	NEGOCIATED_TYPES.TRANSFER,
	NEGOCIATED_TYPES.FLIGHT,
];

const HolidaySummary = ({
	isStopover,
	isSDP,
	isHotelOnly,
	booking = {},
	ficheProduit = {},
	showProductPicture,
	showTimer,
	showBookingBanner,
	luggageMaxCount = 0,
	quotationStatus,
	negotiatedOfSelectedOffer = [],
	activities = [],
	stressMarketingMsg = {},
	priceChangeData = {},
	quotationCode,
	flightOptions = {},
	accommodations = [],
	expandProductDetails = false,
	expandFlightDetails = false,
}) => {
	const [flightCollapseActiveKey, setFlightCollapseActiveKey] = useState([]);

	let productTypeMessage = "booking.holiday.summary.sdp.to.title";

	if (isStopover) {
		productTypeMessage = "booking.holiday.summary.stopover.to.title";
	} else if (isHotelOnly) {
		productTypeMessage = "booking.holiday.summary.hotel.only.to.title";
	}

	const hasQuoteFullPropertyWarning = quotationStatus === QUOTATION_CODE_STATUS.FULL_PROPERTY;

	const { track } = useAnalytics();
	const showStressMarketingMsgFlag = useFsFlag("stress_marketing_message_display", false);
	const showStressMarketingFs =
		showStressMarketingMsgFlag.getValue() && !hasQuoteFullPropertyWarning;

	const transfer = booking.transfer || {};

	// from /booking/restoreSession, we only have departureCity.code
	const departureCityLabel = booking?.departureCity?.label
		? parseDepartureCityLabel(booking?.departureCity?.label)
		: booking?.departureCity?.code;

	const destinationCity =
		ficheProduit?.productExperience === PRODUCT_EXPERIENCES.CIRCUIT
			? ficheProduit?.country
			: booking?.destinationResort?.label ||
			  ficheProduit?.destination?.label ||
			  ficheProduit?.resort;

	const tripAdvisorData = ficheProduit.tripAdvisor || {};
	const holidayCheckData = ficheProduit.holidaycheck || {};

	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const photo = ficheProduit.photos && ficheProduit.photos[0];

	// pour pp, les negotiated sont par type d'offre alors que pour sdp, non
	let negotiatedList = [];
	const {
		isMediumLevelMsg,
		stressMarketingLabel,
		level,
		stressMarketingShortLabel,
	} = stressMarketingMsg;

	const { stopOverCity, stopOverDuration } = booking?.flight?.outbound || {};

	useEffect(() => {
		if (
			level !== false &&
			level !== undefined &&
			stressMarketingLabel !== false &&
			stressMarketingLabel !== undefined &&
			showStressMarketingFs &&
			quotationCode
		) {
			track("stress_marketing_data", {
				level: level,
				message: stressMarketingLabel,
				...(stressMarketingShortLabel !== false &&
					stressMarketingShortLabel !== undefined && {
						shortMessage: stressMarketingShortLabel,
					}),
			});
		}
	}, [quotationCode]);

	if (negotiatedOfSelectedOffer?.length > 0) {
		negotiatedList = negotiatedOfSelectedOffer;
	} else if (ficheProduit?.negotiated?.length > 0) {
		negotiatedList = ficheProduit.negotiated;
	}

	const negotiatedListToDisplay =
		(negotiatedList &&
			negotiatedList.filter(negotiated => {
				return !NEGOS_TO_EXCLUDE.includes(negotiated?.type);
			})) ||
		[];

	const holidaySummaryDetailsClass = classNames("holiday-summary__details", {
		"holiday-summary__details--loading": quotationStatus !== QUOTATION_CODE_STATUS.SUCCESS,
	});

	const accommodationDetailsClassName = classNames(
		"holiday-summary__collapse-content holiday-summary__accommodation-details",
		{
			"holiday-summary__accommodation-details--multiple-accommodations":
				accommodations.length > 1,
		}
	);

	const showTimerOnProductPicture =
		showTimer && (ficheProduit.expiration?.hour > 0 || ficheProduit.expiration?.day > 0);

	const nonIncludedActivitiesList = activities.filter(activity => {
		return activity.guests.every(guest => guest.includedGuest === 0);
	});

	const productName = (
		<ProductNameWithCategory name={ficheProduit.name} category={ficheProduit.category} />
	);
	const showStressMarketingMsg = isMediumLevelMsg
		? showStressMarketingFs && priceChangeData?.priceChangeStatus !== PRICE_CHANGE_STATUS.UP
		: showStressMarketingFs && true;

	const handleFlightCollapseChange = key => setFlightCollapseActiveKey(key);
	const isFlightCollapseExpanded = flightCollapseActiveKey.length > 0;

	const startDate = new Date(booking?.departureDate);
	const endDate = addDays(new Date(booking?.departureDate), booking?.duration?.value);
	const shouldShowStartYear = startDate.getFullYear() !== endDate.getFullYear();
	const shouldDisplayTravelDates = booking?.offer?.type === OFFER_TYPES.ACCOMODATION_ONLY;

	return (
		<div className="holiday-summary" data-testid="holiday-summary">
			{showBookingBanner && <BookingBannerPlaceholderContainer />}

			<div className={holidaySummaryDetailsClass}>
				{showProductPicture && photo && (
					<div className="holiday-summary__picture">
						{isMobile ? (
							<BackgroundProgressiveImg
								sizes={IMG_SIZES_MOBILE}
								src={photo.url}
								alt={photo.alt}
								thumbnail={photo.thumbnail}
								backgroundPosition={"center"}
								productUri={ficheProduit?.uri}
							/>
						) : (
							<ProgressiveImg
								sizes={IMG_SIZES_DESKTOP}
								src={photo.url}
								alt={photo.alt}
								resolution={resolution}
								thumbnail={photo.thumbnail}
								productUri={ficheProduit?.uri}
							/>
						)}

						{showTimerOnProductPicture && (
							<div
								className="holiday-summary__badge"
								data-testid="holiday-summary-badge"
							>
								<Badge>
									<Timer
										hour={ficheProduit.expiration?.hour}
										day={ficheProduit.expiration?.day}
										suffix={<FormattedMessage id="timer.left.label" />}
									/>
								</Badge>
							</div>
						)}
					</div>
				)}

				<div className="holiday-summary__title" data-testid="detail-resort">
					<Typography variant={TYPOGRAPHY_VARIANTS.XL2} isBold>
						<FormattedMessage id={productTypeMessage} />
					</Typography>
				</div>

				<ul className="holiday-summary__list">
					{shouldDisplayTravelDates && (
						<li className="holiday-summary__list-item">
							<div className="holiday-summary__list-item-head">
								<IconCalendar />
								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									isBold
									className="holiday-summary__list-item-head-content"
									component="p"
								>
									<FormattedDate
										value={startDate}
										weekday="short"
										day="2-digit"
										month="short"
										year={shouldShowStartYear ? "numeric" : undefined}
									/>{" "}
									-{" "}
									<FormattedDate
										value={endDate}
										weekday="short"
										day="2-digit"
										month="short"
										year="numeric"
									/>
								</Typography>
							</div>
						</li>
					)}

					<li className="holiday-summary__list-item" data-testid="detail-travellers">
						<div className="holiday-summary__list-item-head">
							<i className="icon icon--travellers" />
							<Typography
								variant={TYPOGRAPHY_VARIANTS.LARGE}
								isBold
								className="holiday-summary__travellers"
								component="p"
							>
								{isSDP ? (
									<OccupanciesDisplayLabel
										occupancies={booking.occupancies}
										showPrefix={false}
									/>
								) : (
									<PassengersCounterDisplay
										adults={booking.adults}
										children={booking.children}
										infants={booking.infants}
									/>
								)}
								<span>/</span>
								<FormattedPlural
									value={accommodations.length}
									one={
										<FormattedMessage
											values={{ count: accommodations.length }}
											id="general.room.count.label.singular"
										/>
									}
									other={
										<FormattedMessage
											values={{ count: accommodations.length }}
											id="general.room.count.label.plural"
										/>
									}
								/>
							</Typography>
						</div>
					</li>

					{departureCityLabel && (
						<li
							className="holiday-summary__list-item"
							data-testid="detail-departureCity"
						>
							<Collapse
								className="holiday-summary__collapse"
								{...expandFlightDetails && { defaultActiveKey: ["0"] }}
								expandIcon={({ isActive }) => {
									return (
										<IconArrowDown
											style={{
												transform: `rotate(${isActive ? 180 : 0}deg)`,
											}}
										/>
									);
								}}
								onChange={handleFlightCollapseChange}
							>
								<Collapse.Panel
									headerClass="holiday-summary__collapse-header"
									header={
										<div className="holiday-summary__list-item-head">
											<IconFlight />
											<div className="holiday-summary__list-item-head-content">
												<Typography
													variant={TYPOGRAPHY_VARIANTS.LARGE}
													isBold
												>
													<FormattedMessage
														id="booking.holiday.summary.to.title"
														values={{
															departureCity: departureCityLabel,
															destinationCity: destinationCity,
														}}
														className="holiday-summary__departureCity"
													/>
												</Typography>

												<TravelDatesContainer
													showDepartureDateForInboundFlight
												/>

												{stopOverDuration &&
													stopOverCity &&
													!isFlightCollapseExpanded && (
														<div className="holiday-summary__stopover-duration">
															<Typography
																variant={TYPOGRAPHY_VARIANTS.SMALL}
																isBold
															>
																<FormattedPlural
																	value={stopOverDuration}
																	one={
																		<FormattedMessage
																			values={{
																				days: (
																					<FormattedMessage
																						values={{
																							duration: stopOverDuration,
																						}}
																						id="general.duration.days.singular"
																					/>
																				),
																			}}
																			id="stop.over.flight.duration.label"
																		/>
																	}
																	other={
																		<FormattedMessage
																			values={{
																				days: (
																					<FormattedMessage
																						values={{
																							duration: stopOverDuration,
																						}}
																						id="general.duration.days.plural"
																					/>
																				),
																			}}
																			id="stop.over.flight.duration.label"
																		/>
																	}
																/>
															</Typography>
														</div>
													)}
											</div>
										</div>
									}
								>
									<div className="holiday-summary__collapse-content">
										<MyFlightDetail
											flight={booking.flight}
											lugaggeCount={booking.luggage?.quantity}
											luggageMaxCount={luggageMaxCount}
											flightOptions={flightOptions}
											maxWeightPerCheckedBaggage={
												booking?.luggage?.maxWeightPerBaggage
											}
										/>
									</div>
								</Collapse.Panel>
							</Collapse>
						</li>
					)}

					<li className="holiday-summary__list-item" data-testid="detail-product-name">
						<Collapse
							className="holiday-summary__collapse"
							{...expandProductDetails && { defaultActiveKey: ["0"] }}
							expandIcon={({ isActive }) => {
								return (
									<IconArrowDown
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<div className="holiday-summary__list-item-head">
										{[
											PRODUCT_EXPERIENCES.CIRCUIT,
											PRODUCT_EXPERIENCES.COMBINE,
											PRODUCT_EXPERIENCES.CRUISE,
											PRODUCT_EXPERIENCES.AUTOTOUR,
										].includes(ficheProduit?.productExperience) ? (
											<i className="icon icon--circuit" />
										) : (
											<i className="icon icon--hotel" />
										)}
										<div className="holiday-summary__list-item-head-content">
											<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
												{productName}
											</Typography>
											<ProductRatingContainer
												locationName={productName}
												tripAdvisor={tripAdvisorData}
												holidayCheck={{
													...holidayCheckData,
													size: "small",
												}}
											/>
											<ProductSummaryContainer />
										</div>
									</div>
								}
							>
								<div className={accommodationDetailsClassName}>
									{accommodations?.length > 0 && (
										<AccommodationSummaryList accommodations={accommodations} />
									)}

									{(transfer?.vehicule?.length > 0 ||
										negotiatedListToDisplay?.length > 0) && (
										<div className="holiday-summary__add-ons-and-negotiated">
											{transfer.vehicule && (
												<div className="holiday-summary__list-item-head holiday-summary__transfer-info">
													<TransferIcon
														width={16}
														height={16}
														aria-hidden="true"
													/>
													<Typography
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
														data-testid="detail-transfer"
														component="p"
													>
														{transfer.vehicule}
													</Typography>
												</div>
											)}

											{!isEmpty(nonIncludedActivitiesList) &&
												nonIncludedActivitiesList.map((activity, index) => {
													return (
														<div
															key={activity.name + index}
															className="holiday-summary__list-item-head"
															data-testid="activities-extra"
														>
															<NegociatedItem label={activity.name} />
														</div>
													);
												})}

											{negotiatedListToDisplay.length > 0 &&
												negotiatedListToDisplay.map((negotiated, index) => {
													return (
														<div
															key={negotiated.label + index}
															className="holiday-summary__list-item-head"
															data-testid="detail-negotiated"
														>
															<NegociatedItem
																label={negotiated.label}
															/>
														</div>
													);
												})}
										</div>
									)}
								</div>
							</Collapse.Panel>
						</Collapse>
					</li>
				</ul>
				{showStressMarketingMsg && stressMarketingLabel && (
					<div className="holiday-summary__stress-marketing">
						<div className="holiday-summary__stress-marketing--icon">
							<IconClockNotification width={16} heigth={16} />
						</div>
						<div className="holiday-summary__stress-marketing--label">
							{stressMarketingLabel}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

HolidaySummary.propTypes = {
	isStopover: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
	isSDP: PropTypes.bool,
	showProductPicture: PropTypes.bool,
	booking: PropTypes.object,
	ficheProduit: PropTypes.object,
	showTimer: PropTypes.bool,
	showBookingBanner: PropTypes.bool,
	luggageMaxCount: PropTypes.number,
	quotationStatus: PropTypes.string,
	negotiatedOfSelectedOffer: PropTypes.array,
	activities: PropTypes.arrayOf(PropTypes.object),
	stressMarketingMsg: PropTypes.object,
	flightOptions: PropTypes.object,
	priceChangeData: PropTypes.object,
	quotationCode: PropTypes.string,
	accommodations: PropTypes.array,
	expandProductDetails: PropTypes.bool,
	expandFlightDetails: PropTypes.bool,
};

export default memo(HolidaySummary);
